import { ClientShortHandName } from '@snoam/mono-scc';
import { getIdentity } from './actions/Actions';
import { IAddress, IReferer, ProductId } from './CustomTypes';
import StorageService from './services/StorageService';
import { ILoginOptions } from '@schibsted/account-sdk-browser';

/**
 * Create a number array from and including start/stop
 */
export function createNumberArray(start: number, stop: number): number[] {
  return Array(stop - start + 1)
    .fill(0)
    .map((_, i) => start + i);
}

/**
 * Create a date string on the format 2016-12-31
 */
export function createDateString(
  day: number | string,
  month: number | string,
  year: number | string
) {
  if (!day || !month || !year) {
    return '';
  }
  return `${year}-${padWithZerosLengthTwo(month)}-${padWithZerosLengthTwo(
    day
  )}`;
}

export function padWithZerosLengthTwo(num: number | string) {
  return ('00' + num).substr(-2, 2);
}

export function padWithZerosLengthFour(num: number | string) {
  return ('0000' + num).substr(-4, 4);
}

function getRelativeUrl() {
  return location.href.substring(0, location.href.indexOf('/products/'));
}

export function getUrlForPage(productId: ProductId, page: string) {
  return `${getRelativeUrl()}/products/${productId}/${page}`;
}

export function createAddCardUrl(
  owningClient: ClientShortHandName | '',
  productId: ProductId,
  spidId: number,
  email: string
) {
  return `/bestilling/c/${owningClient}/api/v2/ff/addPaymentCard.json?productId=${productId}&spidId=${spidId}&email=${encodeURIComponent(
    email
  )}`;
}

export function createStartPageFullUrl(productId: ProductId) {
  return getUrlForPage(productId, '');
}

export function isValidUntilPassed(validUntil?: number) {
  return validUntil && validUntil < Date.now();
}

let lastId = 0;
export function newId() {
  return ++lastId;
}

export function encode(object: object) {
  return window.btoa(JSON.stringify(object));
}

export function decode(object: string) {
  return JSON.parse(window.atob(object));
}

export function createB64data(
  address: IAddress,
  referer: IReferer,
  phoneNumber: string,
  userCreated: boolean,
  email: string
): string {
  const obj: any = {};
  if (address && (address.zipCode || address.zipCode)) {
    obj.address = address;
  }
  if (referer && (referer.referer || referer.redirectUri)) {
    obj.referer = referer;
  }
  if (phoneNumber) {
    obj.phoneNumber = phoneNumber;
  }
  if (userCreated) {
    obj.userCreated = userCreated;
  }
  if (email) {
    obj.email = email;
  }
  return encode(obj);
}

export function urlEncodeObject(object: { [index: string]: string }) {
  const urlEncodedObject: { [index: string]: string } = {};
  Object.keys(object).forEach(it => {
    urlEncodedObject[it] = encodeURIComponent(object[it]);
  });
  return urlEncodedObject;
}

export function isProduction(
  location: Pick<Location, 'hostname'> = window.location
) {
  return !/localhost|test|\.lh$/.test(location.hostname);
}

export function getClientId(
  clientShortHandName: keyof typeof ClientShortHandName | ''
) {
  if (!clientShortHandName) {
    return '';
  }
  const prodClientIds: { [key in ClientShortHandName]: string } = {
    AGR: '53284c56431c7a4fa0000003',
    AP: '520204a6411c7a8838000000',
    ASB: '5c0a6f296f13d509052c548a',
    ASK: '53284a90421c7a5602000004',
    BEDRIFT: '55841aa52d09e5f666ac46b7', // Same as SCH
    BTI: '50cf3a14421c7a895d000004',
    BYN: '53284b28421c7a4f03000003',
    DNB: '5698d5066c09e50a026ab4fe',
    DP: '4fb654a7421c7a0b19000001',
    E24: '4fb654a7421c7a0b19000001', // Same as DP
    FAK: '53284c6a431c7a7fa0000005',
    FVN: '4f911ef0411c7a1739000002',
    KVN: '53284c6a431c7a7fa0000005',
    LIN: '53284c56431c7a4fa0000003',
    MPL: '556ec2cb854e7b72691c97d5',
    SA: '50cf3c40421c7a186400000e',
    SCH: '55841aa52d09e5f666ac46b7',
    STR: '53284b60421c7a6a03000004',
    SYS: '57208f0004367a5920261a32',
    VG: '54d9ffc5cba79ca80f8b456b',
    VNY: '53284b75421c7ad603000003'
  };
  const prodClientId: (k: keyof typeof ClientShortHandName) => string = k =>
    prodClientIds[k];

  const testClientIds: { [key in ClientShortHandName]: string } = {
    AGR: '52f21a45efd04beb12000002',
    AP: '510fb7e1efd04bf00d000000',
    ASB: '5c0e6d3d115e4909118b6d2c',
    ASK: '52f219fcefd04b5f10000002',
    BEDRIFT: '55841a6cc4609cd31f0e79e4', // Same as SCH
    BTI: '5037826aefd04b6471000000',
    BYN: '52f21a05efd04b5311000008',
    DNB: '56a765030277d01d1e50b52d',
    DP: '4f06b422efd04b6a6900000b',
    E24: '4f06b422efd04b6a6900000b', // Same as DP
    FAK: '52f21a3befd04b6911000003',
    FVN: '4f06edcbefd04be41f00000a',
    KVN: '52f21a3befd04b6911000003',
    LIN: '52f21a45efd04beb12000002',
    MPL: '556ec274e93a04c55d606ee5',
    SA: '506c29eaefd04b147c000001',
    SCH: '55841a6cc4609cd31f0e79e4',
    STR: '52f21a16efd04bc30e000002',
    SYS: '57208ecef315bb9f2961dc95',
    VG: '54aa5bf7d067286169efec5b',
    VNY: '52f21a29efd04b062d000002'
  };
  const testClientId: (k: keyof typeof ClientShortHandName) => string = k =>
    testClientIds[k];

  return isProduction()
    ? prodClientId(clientShortHandName)
    : testClientId(clientShortHandName);
}

export function getSchibstedServerName() {
  return isProduction() ? 'payment.schibsted.no' : 'identity-pre.schibsted.com';
}
export function getSchibstedEnvName() {
  return isProduction() ? 'PRO_NO' : 'PRE';
}

export function doSpidLoginLinkWithRedirectToStart(
  owningClient: ClientShortHandName | '',
  productId: ProductId,
  email: string,
  teaser: string
) {
  return getIdentity(owningClient).login({
    loginHint: email,
    redirectUri: `https://us-central1-s-plat.cloudfunctions.net/redirect-helper-${
      isProduction() ? 'prod' : 'stage'
    }`,
    state: JSON.stringify({
      redirectUri: `${getUrlForPage(
        productId,
        'start'
      )}?${getRedirectUrlInLocalStorage()}`
    }),
    teaser
  } as ILoginOptions);
}

export function createLoginSpidLoginLinkWithRedirectUrl(
  owningClient: ClientShortHandName | '',
  email: string,
  redirectUrl: string,
  spidTeaser: string
) {
  const redirectState = JSON.stringify({
    redirectUri: `${redirectUrl}`
  });
  return getIdentity(owningClient).loginUrl(
    redirectState,
    undefined,
    'openid',
    `https://us-central1-s-plat.cloudfunctions.net/redirect-helper-${
      isProduction() ? 'prod' : 'stage'
    }`,
    true,
    undefined,
    undefined,
    spidTeaser
  );
}

export function toTitleCase(str?: string) {
  if (!str) {
    return '';
  }
  return str.replace(/\w\S*/g, txt => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

export function triggerHotjar(trigger: string, eventName: string) {
  if (window && (window as any).hj) {
    (window as any).hj(trigger, eventName);
  }
}

export interface ILocalStorageData {
  utmCampaign: string;
  utmContent: string;
  utmMedium: string;
  utmSource: string;
  utmTerm: string;
  referer: string;
  redirectUri: string;
  __rct?: string;
}

const storageService = new StorageService();

export function setRedirectUrlInLocalStorage(data: ILocalStorageData) {
  storageService.setItem('ff_url_data', JSON.stringify(data));
}

export function getRedirectDataFromLocalStorage(): ILocalStorageData {
  const localStorageData: ILocalStorageData = JSON.parse(
    storageService.getItem('ff_url_data')
  );
  if (localStorageData) {
    return localStorageData;
  }
}

export function getRedirectUrlInLocalStorage() {
  const localStorageData: ILocalStorageData = JSON.parse(
    storageService.getItem('ff_url_data')
  );
  if (localStorageData) {
    /* tslint:disable-line:max-line-length */
    const utmTags = `utm_campaign=${encodeURIComponent(
      localStorageData.utmCampaign
    )}&utm_content=${encodeURIComponent(
      localStorageData.utmContent
    )}&utm_medium=${encodeURIComponent(
      localStorageData.utmMedium
    )}&utm_source=${encodeURIComponent(
      localStorageData.utmSource
    )}&utm_term=${encodeURIComponent(localStorageData.utmTerm)}`;
    let rct = '';
    if (localStorageData.__rct) {
      rct = `&__rct=${localStorageData.__rct}`;
    }
    return `redirect_uri=${encodeURIComponent(
      localStorageData.redirectUri
    )}&referer=${encodeURIComponent(
      localStorageData.referer
    )}&${utmTags}${rct}`;
  } else {
    return null;
  }
}



// WEBPACK FOOTER //
// ./src/fastflowUtils.ts